<template>
  <CCard>
    <CCardHeader>
      Tunnel Endpoints
    </CCardHeader>
    <CCardBody>
      <CRow>
        <CCol sm="12">
          <CButton
            color="success"
            square
            class="mb-3"
            @click="openAddModal"
          >
            Add Endpoint
          </CButton>
          <CDataTable
            hover
            small
            fixed
            table-filter
            sorter
            items-per-page-select
            :items-per-page="15"
            :fields="fields"
            :items="endpoints"
            :noItemsView="{ noItems: 'No Item~' }"
            pagination
          >
            <template #owner_type="{item}">
              <td class="py-2">
                <CBadge :color="ownerBadgeColor(item.owner_type)">
                  {{ item.owner_type }}
                </CBadge>
              </td>
            </template>
            <template #address_family="{item}">
              <td class="py-2">
                <CBadge :color="item.address_family === 'ipv6' ? 'info' : 'secondary'">
                  {{ item.address_family === 'ipv6' ? 'IPv6' : 'IPv4' }}
                </CBadge>
              </td>
            </template>
            <template #owner="{item}">
              <td class="py-2">
                {{ ownerName(item) }}
              </td>
            </template>
            <template #action="{item}">
              <td class="py-2">
                <CButton
                  color="danger"
                  square
                  @click="openDeleteModal(item)"
                >
                  Delete
                </CButton>
              </td>
            </template>
          </CDataTable>
        </CCol>
      </CRow>
    </CCardBody>
    <CToaster :autohide="3000">
      <template v-for="toast in fixedToasts">
        <CToast
          :key="'toast' + toast"
          :show="true"
          :color="alertLevel"
          header="Alert"
        >
          {{ returnMsg }}
        </CToast>
      </template>
    </CToaster>
    <CModal
      title="Message"
      color="primary"
      :show.sync="requestMsgModal"
    >
      {{ requestMsg }}
      <template #footer>
        <CButton @click="requestMsgModal=false" color="primary">Close</CButton>
      </template>
    </CModal>

    <!-- Add Endpoint -->
    <CModal
      title="Add Endpoint"
      color="success"
      :show.sync="addModal"
    >
      <CForm>
        <CAlert color="info" fade>
          端点创建后不可修改，只能删除（被隧道引用的端点不可删除）。
        </CAlert>
        <CSelect
          label="Owner Type"
          :options="ownerTypeOptions"
          :value.sync="addForm.owner_type"
          @change="onOwnerTypeChange"
        />
        <div class="form-group" v-if="addForm.owner_type === 'router'">
          <label class="form-label">Router</label>
          <SearchSelect :options="routerOptions" v-model="addForm.router_id" />
        </div>
        <div class="form-group" v-if="addForm.owner_type === 'cmdb'">
          <label class="form-label">CMDB Device</label>
          <SearchSelect :options="deviceOptions" v-model="addForm.cmdb_id" />
        </div>
        <CSelect
          label="Address Family"
          :options="familyOptions"
          :value.sync="addForm.address_family"
        />
        <CInput
          label="Address (domain or IP)"
          v-model="addForm.address"
          placeholder="example.com / 1.2.3.4 / 240d::1"
        />
        <CInput
          label="Remark (端点用途)"
          v-model="addForm.remark"
        />
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="secondary" @click="addModal=false">Cancel</CButton>
          <CButton color="success" @click="addEndpoint">Create</CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- Delete Confirm -->
    <CModal
      title="Delete Endpoint"
      color="danger"
      :show.sync="deleteModal"
    >
      <CAlert color="danger" fade>
        确定要删除端点「{{ deleteTarget.address }}」吗？被隧道引用的端点无法删除。
      </CAlert>
      <template #footer>
        <CButtonGroup>
          <CButton color="secondary" @click="deleteModal=false">Cancel</CButton>
          <CButton color="danger" @click="deleteEndpoint">Delete</CButton>
        </CButtonGroup>
      </template>
    </CModal>
  </CCard>
</template>

<script>
const fields = [
  { key: "id", label: "ID" },
  { key: "owner_type", label: "Owner Type" },
  { key: "owner", label: "Owner" },
  { key: "address", label: "Address" },
  { key: "address_family", label: "Family" },
  { key: "remark", label: "Remark" },
  {
    key: "action",
    label: "Action",
    sorter: false,
    filter: false,
    _style: "width: 12%"
  }
];

export default {
  name: "TunnelEndpoints",
  data() {
    return {
      fixedToasts: 0,
      returnMsg: "",
      alertLevel: "success",
      requestMsgModal: false,
      fields,
      endpoints: [],
      routers: [],
      devices: [],
      addModal: false,
      deleteModal: false,
      deleteTarget: { id: null, address: '' },
      ownerTypeOptions: [
        { value: "router", label: "Router" },
        { value: "cmdb", label: "CMDB Device" },
        { value: "external", label: "External" }
      ],
      familyOptions: [
        { value: "ipv4", label: "IPv4" },
        { value: "ipv6", label: "IPv6" }
      ],
      addForm: {
        owner_type: "external",
        router_id: null,
        cmdb_id: null,
        address: "",
        address_family: "ipv4",
        remark: ""
      }
    };
  },
  computed: {
    routerOptions: function() {
      let options = [{ label: "Select a router" }];
      this.routers.map(item => {
        options.push({ value: item.id, label: item.name + "(" + item.host + ")" });
      });
      return options;
    },
    deviceOptions: function() {
      let options = [{ label: "Select a device" }];
      this.devices.map(item => {
        options.push({ value: item.id, label: item.name + "(#" + item.id + ")" });
      });
      return options;
    }
  },
  mounted: function() {
    this.onLoadPage();
  },
  methods: {
    ownerBadgeColor: function(type) {
      if (type === "router") return "info";
      if (type === "cmdb") return "primary";
      return "secondary";
    },
    ownerName: function(item) {
      if (item.owner_type === "router") {
        return item.router ? item.router.name : "router#" + item.router_id;
      }
      if (item.owner_type === "cmdb") {
        return item.cmdb ? item.cmdb.name : "device#" + item.cmdb_id;
      }
      return "External";
    },
    onLoadPage: function() {
      this.$axios.get("user/tunnel/endpoint/list", {})
        .then((response) => {
          if (response.data.code === 200) {
            this.endpoints = response.data.endpoints.map(item => {
              return { ...item, owner: this.ownerName(item) };
            });
          }
        });

      this.$axios.get("user/router", {})
        .then((response) => {
          if (response.data.code === 200) {
            this.routers = response.data.userRouter;
          }
        });

      this.$axios.get("user/cmdb/device/list", {})
        .then((response) => {
          if (response.data.code === 200) {
            this.devices = response.data.devices;
          }
        });
    },
    onOwnerTypeChange: function() {
      this.addForm.router_id = null;
      this.addForm.cmdb_id = null;
    },
    openAddModal: function() {
      this.addForm = {
        owner_type: "external",
        router_id: null,
        cmdb_id: null,
        address: "",
        address_family: "ipv4",
        remark: ""
      };
      this.addModal = true;
    },
    addEndpoint: function() {
      if (!this.addForm.address) {
        this.requestMsg = "Address can not be null";
        this.requestMsgModal = true;
        return;
      }
      if (this.addForm.owner_type === "router" && !this.addForm.router_id) {
        this.requestMsg = "Please select a router";
        this.requestMsgModal = true;
        return;
      }
      if (this.addForm.owner_type === "cmdb" && !this.addForm.cmdb_id) {
        this.requestMsg = "Please select a device";
        this.requestMsgModal = true;
        return;
      }
      this.$axios.post("user/tunnel/endpoint/add", {
        owner_type: this.addForm.owner_type,
        router_id: this.addForm.router_id,
        cmdb_id: this.addForm.cmdb_id,
        address: this.addForm.address,
        address_family: this.addForm.address_family,
        remark: this.addForm.remark
      })
        .then((response) => {
          if (response.data.code === 200) {
            this.addModal = false;
            this.alertLevel = "success";
            this.returnMsg = response.data.msg;
            this.fixedToasts++;
            this.onLoadPage();
          } else {
            this.addModal = false;
            this.requestMsg = response.data.msg;
            this.requestMsgModal = true;
          }
        });
    },
    openDeleteModal: function(item) {
      this.deleteTarget = { id: item.id, address: item.address };
      this.deleteModal = true;
    },
    deleteEndpoint: function() {
      this.$axios.delete("user/tunnel/endpoint/" + this.deleteTarget.id + "/delete", {})
        .then((response) => {
          if (response.data.code === 200) {
            this.deleteModal = false;
            this.alertLevel = "success";
            this.returnMsg = response.data.msg;
            this.fixedToasts++;
            this.onLoadPage();
          } else {
            this.deleteModal = false;
            this.requestMsg = response.data.msg;
            this.requestMsgModal = true;
          }
        });
    },
  }
};
</script>
